import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import type { AddRowInput, CreateFMEAOutput, DeleteRowInput, FetchGridInput, FetchGridOutput, FetchGridStreamInput, MoveRowInput, UpdateRowInput, UpdateGenerateStatusInput } from '@/types/index';

export const GridApi = Symbol('GridApi');

export interface GridApi {
    fetchGridData(input: FetchGridInput): Promise<FetchGridOutput>
    updateGenerateStatus(input: UpdateGenerateStatusInput): Promise<any>
    addGridRow(input: AddRowInput): Promise<string>
    updateGridData(input: UpdateRowInput): Promise<any>
    deleteGridData(input: DeleteRowInput): Promise<any>
    moveGridData(input: MoveRowInput): Promise<any>
    exportGridData(input: CreateFMEAOutput): Promise<string>
    fetchStreamGridData<T>(input: FetchGridStreamInput): PsHttpSSE<T>
}

@injectable()
export class GridApiImpl extends PsHttpService implements GridApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    fetchGridData(input: FetchGridInput): Promise<FetchGridOutput> {
        return this.get<PatSnapCommonResponseData<FetchGridOutput>>({
            url: `/fmea/${input.fmea_id}`,
        }).then((res) => {
            return res.data as FetchGridOutput
        });
    }

    updateGenerateStatus(input: UpdateGenerateStatusInput): Promise<any> {
        const { fmea_id, generate_status, stage } = input
        return this.put<PatSnapCommonResponseData<any>>({
            url: `/fmea/${fmea_id}/generate-status`,
            data: {
                generate_status,
                stage,
            },
        }).then((res) => {
            return res.data
        });
    }

    addGridRow(input: AddRowInput): Promise<string> {
        const { fmea_id, pre_row_id, ...rows } = input
        return this.post<PatSnapCommonResponseData<string>>({
            url: `/fmea/${fmea_id}/row/add`,
            data: {
                pre_row_id,
                ...rows,
            },
        }).then((res) => {
            return res.data
        });
    }

    updateGridData(input: UpdateRowInput): Promise<any> {
        const { fmea_id, row_id, ...rows } = input
        return this.put<PatSnapCommonResponseData<any>>({
            url: `/fmea/${fmea_id}/row/update`,
            data: {
                row_id,
                ...rows,
            },
        }).then((res) => {
            return res.data
        });
    }

    deleteGridData(input: DeleteRowInput): Promise<any> {
        const { fmea_id, row_ids } = input
        return this.delete<PatSnapCommonResponseData<any>>({
            url: `/fmea/${fmea_id}/row`,
            data: {
                row_ids,
            },
        }).then((res) => {
            return res.data
        });
    }

    moveGridData(input: MoveRowInput): Promise<any> {
        const { fmea_id, row_id, pre_row_id } = input
        return this.put<PatSnapCommonResponseData<any>>({
            url: `/fmea/${fmea_id}/row/order`,
            data: {
                pre_row_id,
                row_id,
            },
        }).then((res) => {
            return res.data
        });
    }

    exportGridData(input: CreateFMEAOutput): Promise<string> {
        const { fmea_id } = input
        return this.get<PatSnapCommonResponseData<any>>({
            url: `/fmea/${fmea_id}/excel`,
        }).then((res) => {
            return res.data
        });
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    fetchStreamGridData<T>(input: FetchGridStreamInput): PsHttpSSE<T> {
        return this._factorySsePsHttp<T>().init({
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/sse/fmea/generate`,
            data: input,
            hasFinalMessage: (data) => !!data.row_data,
        })
    }
}
